import React, { useEffect, useState } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import {
    ASSESSMENT_OPTIONS,
    CURRICULUM_KIND_OPTIONS,
    LESSON_TYPE_OPTIONS
} from '../utils/directionCurriculum';
import './DirectionForm.css';

const emptyTheme = () => ({
    title: '',
    hours_count: '',
    lesson_type: 'group',
    with_attestation: false,
    attestation_type: ''
});

const DirectionForm = ({ direction, onSubmit, onCancel, loading }) => {
    const { config } = useConfig();
    const curriculumEnabled = config?.code === 'mag';
    const [form, setForm] = useState({});
    const [themes, setThemes] = useState([]);

    useEffect(() => {
        setForm({
            name: direction?.name || '',
            full_name: direction?.full_name || '',
            short_name: direction?.short_name || '',
            page_link: direction?.page_link || '',
            description: direction?.description || '',
            hours_count: direction?.hours_count ?? '',
            installment_cost: direction?.installment_cost ?? '',
            full_payment_cost: direction?.full_payment_cost ?? '',
            assessment_type: direction?.assessment_type || '',
            curriculum_kind: direction?.curriculum_kind || ''
        });
        setThemes((direction?.themes || []).map((theme) => ({
            title: theme.title || '',
            hours_count: theme.hours_count ?? '',
            lesson_type: theme.lesson_type || 'group',
            with_attestation: Boolean(theme.with_attestation),
            attestation_type: theme.attestation_type || ''
        })));
    }, [direction]);

    const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    const updateTheme = (index, patch) => {
        setThemes((prev) => prev.map((theme, i) => (i === index ? { ...theme, ...patch } : theme)));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!curriculumEnabled) {
            onSubmit({
                name: form.name,
                full_name: form.full_name,
                short_name: form.short_name,
                page_link: form.page_link,
                description: form.description
            });
            return;
        }
        onSubmit({ ...form, themes });
    };

    return (
        <form onSubmit={handleSubmit} className="form direction-form">
            <label>Название *</label>
            <input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} required />

            <label>Полное название</label>
            <input value={form.full_name || ''} onChange={(e) => setField('full_name', e.target.value)} />

            <label>Короткое название</label>
            <input value={form.short_name || ''} onChange={(e) => setField('short_name', e.target.value)} />

            <label>Ссылка на страницу</label>
            <input type="url" value={form.page_link || ''} onChange={(e) => setField('page_link', e.target.value)} />

            <label>Описание</label>
            <textarea rows={4} value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />

            {curriculumEnabled && <div className="direction-form-grid">
                <label>
                    Часы модуля
                    <input type="number" min="0" step="0.01" value={form.hours_count} onChange={(e) => setField('hours_count', e.target.value)} />
                </label>
                <label>
                    Стоимость в рассрочку
                    <input type="number" min="0" step="0.01" value={form.installment_cost} onChange={(e) => setField('installment_cost', e.target.value)} />
                </label>
                <label>
                    Полная стоимость
                    <input type="number" min="0" step="0.01" value={form.full_payment_cost} onChange={(e) => setField('full_payment_cost', e.target.value)} />
                </label>
                <label>
                    Аттестация
                    <select value={form.assessment_type || ''} onChange={(e) => setField('assessment_type', e.target.value)}>
                        <option value="">Не указана</option>
                        {ASSESSMENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                </label>
                <label>
                    Вид модуля
                    <select value={form.curriculum_kind || ''} onChange={(e) => setField('curriculum_kind', e.target.value)}>
                        <option value="">Не указан</option>
                        {CURRICULUM_KIND_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                </label>
            </div>}

            {curriculumEnabled && <>
            <div className="direction-themes-head">
                <h3>Темы</h3>
                <button type="button" className="btn btn-secondary" onClick={() => setThemes((prev) => [...prev, emptyTheme()])}>
                    + Тема
                </button>
            </div>

            {themes.map((theme, index) => (
                <div className="direction-theme-row" key={index}>
                    <span className="direction-theme-index">{index + 1}</span>
                    <input
                        placeholder="Название темы"
                        value={theme.title}
                        onChange={(e) => updateTheme(index, { title: e.target.value })}
                    />
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Часы"
                        value={theme.hours_count}
                        onChange={(e) => updateTheme(index, { hours_count: e.target.value })}
                    />
                    <select value={theme.lesson_type} onChange={(e) => updateTheme(index, { lesson_type: e.target.value })}>
                        {LESSON_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <label className="direction-theme-check">
                        <input
                            type="checkbox"
                            checked={theme.with_attestation}
                            onChange={(e) => updateTheme(index, { with_attestation: e.target.checked })}
                        />
                        Аттестация
                    </label>
                    {theme.with_attestation && (
                        <select value={theme.attestation_type} onChange={(e) => updateTheme(index, { attestation_type: e.target.value })}>
                            <option value="">Тип</option>
                            {ASSESSMENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    )}
                    <button type="button" className="btn-icon" title="Удалить тему" onClick={() => setThemes((prev) => prev.filter((_, i) => i !== index))}>
                        ✕
                    </button>
                </div>
            ))}
            {!themes.length && <p className="direction-themes-empty">Тем пока нет.</p>}
            </>}

            <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>Отмена</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Сохранение...' : (direction ? 'Обновить' : 'Создать')}
                </button>
            </div>
        </form>
    );
};

export default DirectionForm;
